/**
 * Conditional Logic Engine
 *
 * Evaluates show_if conditions for addon services
 * Uses memoization for performance
 *
 * @package KTVHVN_Course_Registration
 * @since 1.1.0
 */

(function($) {
    'use strict';

    window.KTVHVN = window.KTVHVN || {};

    /**
     * Conditional Engine
     */
    window.KTVHVN.ConditionalEngine = class {
        constructor() {
            this.cache = new Map();
        }

        /**
         * Evaluate show_if condition
         *
         * Logic:
         * - If services array is empty OR services contains current service → Pass
         * - If industries array is empty OR industries contains current industry → Pass
         * - Both must pass for addon to be shown
         *
         * @param {Object} condition {services: [], industries: []}
         * @param {Object} state {service: '', industry: ''}
         * @return {Boolean}
         */
        evaluate(condition, state) {
            // Generate cache key
            const cacheKey = this.getCacheKey(condition, state);

            // Check cache
            if (this.cache.has(cacheKey)) {
                return this.cache.get(cacheKey);
            }

            // Evaluate
            const result = this.evaluateCondition(condition, state);

            // Cache result
            this.cache.set(cacheKey, result);

            return result;
        }

        /**
         * Evaluate condition logic
         *
         * @param {Object} condition
         * @param {Object} state
         * @return {Boolean}
         */
        evaluateCondition(condition, state) {
            if (!condition) {
                return true; // No condition = always show
            }

            // Check service condition
            const serviceMatch = this.checkServiceCondition(
                condition.services || [],
                state.service
            );

            // Check industry condition
            const industryMatch = this.checkIndustryCondition(
                condition.industries || [],
                state.industry
            );

            // Both must pass
            return serviceMatch && industryMatch;
        }

        /**
         * Check if service matches condition
         *
         * @param {Array} allowedServices
         * @param {String} currentService
         * @return {Boolean}
         */
        checkServiceCondition(allowedServices, currentService) {
            // Empty array = no restriction
            if (!allowedServices || allowedServices.length === 0) {
                return true;
            }

            // No service selected = fail
            if (!currentService) {
                return false;
            }

            // Check if current service is in allowed list
            return allowedServices.indexOf(currentService) !== -1;
        }

        /**
         * Check if industry matches condition
         *
         * @param {Array} allowedIndustries
         * @param {String} currentIndustry
         * @return {Boolean}
         */
        checkIndustryCondition(allowedIndustries, currentIndustry) {
            // Empty array = no restriction
            if (!allowedIndustries || allowedIndustries.length === 0) {
                return true;
            }

            // No industry selected = still pass if empty array
            if (!currentIndustry) {
                return allowedIndustries.length === 0;
            }

            // Check if current industry is in allowed list
            return allowedIndustries.indexOf(currentIndustry) !== -1;
        }

        /**
         * Generate cache key
         *
         * @param {Object} condition
         * @param {Object} state
         * @return {String}
         */
        getCacheKey(condition, state) {
            return JSON.stringify({
                services: (condition.services || []).sort(),
                industries: (condition.industries || []).sort(),
                current_service: state.service || '',
                current_industry: state.industry || ''
            });
        }

        /**
         * Clear cache
         */
        clearCache() {
            this.cache.clear();
        }

        /**
         * Get cache statistics (for debugging)
         */
        getCacheStats() {
            return {
                size: this.cache.size,
                entries: Array.from(this.cache.entries())
            };
        }
    };

})(jQuery);
